import * as React from 'react';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  CardPreview,
  Title3,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Badge,
} from '@fluentui/react-components';
import { ExtracaoDetalhe } from '../../types/diagnostico';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import { getSeverityBadgeColor, getSeverityText } from '../../utils/severityHelpers';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
    '@media (max-width: 768px)': {
      padding: tokens.spacingHorizontalM,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: tokens.spacingVerticalS,
    },
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  percentualBadge: {
    fontSize: tokens.fontSizeBase300,
    marginLeft: tokens.spacingVerticalXL,
    '@media (max-width: 768px)': {
      marginLeft: 0,
      marginTop: tokens.spacingVerticalXS,
    },
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    gap: tokens.spacingHorizontalS,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileCards: {
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  mobileCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalL,
    paddingVertical: tokens.spacingVerticalL,
  },
  mobileCardItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalXS,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  mobileCardLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
  },
  mobileCardValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  tableHeader: {
    width: '50%',
  },
  tableRegra: {
    width: '10%',
  },
  tableMeta: {
    width: '10%',
  },
  tableTotal: {
    width: '10%',
  },
  tableSeverity: {
    width: '10%',
  },
  tableDate: {
    width: '10%',
  },
  statusBadge: {
    fontSize: tokens.fontSizeBase200,
  },
  actionCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
});

interface ExtractionDetailsProps {
  projetoId: number;
  eventoId: number;
  eventoDescricao: string;
  percentualAtingimento: number;
  extracoes: ExtracaoDetalhe[];
  onBack: () => void;
  onRefresh?: () => void;
}

const ExtractionDetails: React.FC<ExtractionDetailsProps> = ({
  projetoId,
  eventoId,
  eventoDescricao,
  percentualAtingimento,
  extracoes,
  onBack,
  onRefresh,
}) => {
  const styles = useStyles();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getRegraComparacaoText = (regra: number): string => {
    switch (regra) {
      case 1:
        return 'Maior';
      case 2:
        return 'Menor';
      case 3:
        return 'Igual';
      default:
        return 'N/A';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={onBack}
          className={styles.backButton}
        >
          Voltar
        </Button>
        <div>
          <Title3 className={styles.title}>{eventoDescricao}</Title3>
          <Badge
            appearance="filled"
            color={percentualAtingimento >= 80 ? 'success' : percentualAtingimento >= 60 ? 'warning' : 'danger'}
            className={styles.percentualBadge}
          >
            {percentualAtingimento.toFixed(1)}% de atingimento
          </Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <Title3>Detalhes das Extrações</Title3>
        </CardHeader>
        <CardPreview>
          {extracoes && extracoes.length > 0 ? (
            (() => {
              // Se não estiver 100%, mostrar apenas as recomendações (itens que não pontuaram)
              // Se estiver 100%, mostrar todos os itens
              const itensExibir = percentualAtingimento >= 100 
                ? extracoes 
                : extracoes.filter(extracao => extracao.pontuacaoValidada === 0);
              
              return (
                <>
                  {/* Versão Desktop - Tabela */}
                  <Table className={styles.table}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className={styles.tableHeader}>Detalhe da Estrutura</TableHeaderCell>
                        <TableHeaderCell className={styles.tableRegra}>Regra</TableHeaderCell>
                        <TableHeaderCell className={styles.tableMeta}>Meta</TableHeaderCell>
                        <TableHeaderCell className={styles.tableTotal}>Total em uso</TableHeaderCell>
                        <TableHeaderCell className={styles.tableSeverity}>Severidade</TableHeaderCell>
                        <TableHeaderCell className={styles.tableDate}>Data</TableHeaderCell>
                        {percentualAtingimento >= 100 && (
                          <TableHeaderCell className={styles.tableRegra}>Status</TableHeaderCell>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itensExibir.map((extracao, index) => {
                        const estruturaEvento = extracao.estruturaEvento;
                        const detalhe = estruturaEvento?.detalhe || 'Detalhe não informado';
                        const regraComparacao = estruturaEvento?.regraComparacao || 0;
                        const meta = estruturaEvento?.meta ?? '-';
                        const severidade = (estruturaEvento?.severidade ?? 0) as 0 | 1 | 2 | 3;

                        return (
                          <TableRow key={`${extracao.id}-${index}`}>
                            <TableCell>
                              <TableCellLayout>
                                <Text>{detalhe}</Text>
                              </TableCellLayout>
                            </TableCell>
                            <TableCell>{getRegraComparacaoText(regraComparacao)}</TableCell>
                            <TableCell>{meta}</TableCell>
                            <TableCell>{extracao.totalEmUso}</TableCell>
                            <TableCell>
                              <Badge appearance="filled" color={getSeverityBadgeColor(severidade)}>
                                {getSeverityText(severidade)}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(extracao.data)}</TableCell>
                            {percentualAtingimento >= 100 && (
                              <TableCell>
                                <Badge
                                  appearance="filled"
                                  color={extracao.pontuacaoValidada > 0 ? 'success' : 'danger'}
                                >
                                  {extracao.pontuacaoValidada > 0 ? 'OK' : 'Atenção'}
                                </Badge>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Versão Mobile - Cards */}
                  <div className={styles.mobileCards} style={{ display: 'none' }}>
                    {itensExibir.map((extracao, index) => {
                      const estruturaEvento = extracao.estruturaEvento;
                      const detalhe = estruturaEvento?.detalhe || 'Detalhe não informado';
                      const regraComparacao = estruturaEvento?.regraComparacao || 0;
                      const meta = estruturaEvento?.meta ?? '-';
                      const severidade = (estruturaEvento?.severidade ?? 0) as 0 | 1 | 2 | 3;

                      return (
                        <Card key={`${extracao.id}-${index}-mobile`} className={styles.mobileCard}>
                          <CardPreview>
                            <div className={styles.mobileCardItem}>
                              <Text className={styles.mobileCardLabel}>Detalhe da Estrutura:</Text>
                              <Text className={styles.mobileCardValue}>{detalhe}</Text>
                            </div>
                            <div className={styles.mobileCardItem}>
                              <Text className={styles.mobileCardLabel}>Regra:</Text>
                              <Text className={styles.mobileCardValue}>{getRegraComparacaoText(regraComparacao)}</Text>
                            </div>
                            <div className={styles.mobileCardItem}>
                              <Text className={styles.mobileCardLabel}>Meta:</Text>
                              <Text className={styles.mobileCardValue}>{meta}</Text>
                            </div>
                            <div className={styles.mobileCardItem}>
                              <Text className={styles.mobileCardLabel}>Total em uso:</Text>
                              <Text className={styles.mobileCardValue}>{extracao.totalEmUso}</Text>
                            </div>
                            <div className={styles.mobileCardItem}>
                              <Text className={styles.mobileCardLabel}>Severidade:</Text>
                              <Badge appearance="filled" color={getSeverityBadgeColor(severidade)}>
                                {getSeverityText(severidade)}
                              </Badge>
                            </div>
                            <div className={styles.mobileCardItem}>
                              <Text className={styles.mobileCardLabel}>Data:</Text>
                              <Text className={styles.mobileCardValue}>{formatDate(extracao.data)}</Text>
                            </div>
                            {percentualAtingimento >= 100 && (
                              <div className={styles.mobileCardItem}>
                                <Text className={styles.mobileCardLabel}>Status:</Text>
                                <Badge
                                  appearance="filled"
                                  color={extracao.pontuacaoValidada > 0 ? 'success' : 'danger'}
                                >
                                  {extracao.pontuacaoValidada > 0 ? 'OK' : 'Atenção'}
                                </Badge>
                              </div>
                            )}
                          </CardPreview>
                        </Card>
                      );
                    })}
                  </div>
                </>
              );
            })()
          ) : (
            <Text style={{ textAlign: 'center', padding: tokens.spacingVerticalXL }}>
              Nenhuma extração encontrada para este evento.
            </Text>
          )}
        </CardPreview>
      </Card>
    </div>
  );
};

export default ExtractionDetails;